// "The window of our soul": a cluttered room of things and words → swipe the shutters open → the universe, as above so below, a fish that flies → one sun with a thousand names → the painting.
export const soulWindow = {
  slug: "the-window-of-our-soul",
  duration: 32,
  captionTail: "Who is to say a fish can't dream of flying?",
  stages: [
    { id: "clutter", at: 0, hint: "a crowded room" },
    { id: "open", at: 6.5, hint: "swipe to open the window" },
    { id: "universe", at: 9.5, hint: "tap the water" },
    { id: "onesun", at: 19.5, hint: "one sun" },
    { id: "settle", at: 27.5, hint: "" },
  ],
  hold: { at: 8, need: 1, auto: 14 },
  touch: { kind: "shutter", from: 6.5, until: 21 },
  // painted panorama: once the shutters open: the universe, as above so below, a fish that flies, one sun
  layers: [
    { src: "/arcs/soul/clutter.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, show: [-1, 0, 6.8, 9] },
    { src: "/arcs/soul/open.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, glow: 0.2, show: [6.5, 8.8, 10.5, 13] },
    { src: "/arcs/soul/universe.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, water: 0.42, shimmer: 0.2, show: [10, 13, 99, 99] },
  ],
  words: { from: 0.8, to: 10.5, stillAt: 8, list: ["mine", "pride", "different", "right", "them", "mine", "pride"] },
  names: { from: 20, to: 27.5, end: 30 },
  line: { text: "The sun has a thousand names, yet it is one sun.", from: 23.5, to: 31.5, pos: "low" },
  cues: [[0.3, "ticks"], [8.15, "pop"], [8.7, "pop"], [12, "fish"], [15.5, "fish"], [20.2, "pop"]],
  ambient: { scene: "clutter", until: 99, then: [[8.1, "cosmos"]] },
  look: { start: 0.05, lift: [6, 12], end: 0.14, yaw: [[0, -0.8], [4, 0.6], [7, 0], [32, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "soul", pos: "low" },
  speed: [[0, 1], [32, 1]],
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform vec4 uMark[8];
uniform float uT, uPhase, uAspect, uTileH, uRadius, uCy, uSurface, uStill;
varying vec3 vPos;
${"__NOISE__"}
float hash1(float n){ return fract(sin(n * 91.7) * 43758.5453); }
float hash2(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
vec3 tap(vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return texture2D(uMap, mix(uCrop.xy, uCrop.zw, m)).rgb;
}
// a four-pointed glint of starlight: two thin crossing streaks
float glint(vec2 p, float s){ return exp(-abs(p.x) * 90.0 / s - abs(p.y) * 9.0 / s) + exp(-abs(p.y) * 90.0 / s - abs(p.x) * 9.0 / s); }
vec3 sky(vec2 P){
  float y = P.y;
  float sw = snoise(vec3(P.x * 0.35 + y * 0.2, y * 0.5, uT * 0.03)) * 0.5 + 0.5;
  vec3 c = mix(vec3(0.24, 0.17, 0.5), vec3(0.06, 0.07, 0.25), smoothstep(1.9, 6.5, y));
  c = mix(c, vec3(0.45, 0.3, 0.62), smoothstep(0.65, 0.9, sw) * 0.5);
  vec2 sc = floor(P / 0.55);
  vec2 sq = P - (sc + vec2(hash2(sc), hash2(sc + 1.7))) * 0.55;
  c += vec3(1.0, 0.95, 0.85) * glint(sq, 0.6 + 0.6 * hash2(sc + 3.1)) * step(0.55, hash2(sc + 9.3)) * 0.5;
  // planets with their rings
  for (int i = 0; i < 6; i++){
    float fi = float(i);
    vec2 pq = P - vec2(-9.5 + fi * 3.8, 3.7 + 0.9 * hash1(fi + 3.0));
    float pr = 0.12 + 0.12 * hash1(fi + 9.0);
    vec3 pcol = 0.45 + 0.4 * cos(6.2832 * (vec3(0.0, 0.33, 0.67) + hash1(fi)));
    c = mix(c, pcol * (0.6 + 0.5 * smoothstep(pr, -pr, pq.x + pq.y)), 1.0 - smoothstep(pr - 0.01, pr, length(pq)));
    float ring = 1.0 - smoothstep(0.008, 0.018, abs(length(vec2(pq.x, (pq.y + pq.x * 0.25) * 3.5)) - pr * 1.7));
    c = mix(c, vec3(0.9, 0.82, 0.65), ring * step(0.5, hash1(fi + 5.0)) * (step(pr, length(pq)) + step(0.0, pq.y)) * 0.8);
  }
  // the sun and the moon side by side, one light
  vec2 sp = P - vec2(-0.8, 3.1);
  float sd = length(sp);
  c += vec3(1.0, 0.7, 0.25) * exp(-sd * 2.2) * 0.6;
  float rays = 0.5 + 0.5 * sin(atan(sp.y, sp.x) * 14.0 + snoise(vec3(sp * 2.0, uT * 0.1)) * 1.5);
  c = mix(c, vec3(1.0, 0.65, 0.2), (1.0 - smoothstep(0.42, 0.62, sd)) * smoothstep(0.36, 0.42, sd) * rays * 0.7);
  c = mix(c, mix(vec3(1.0, 0.92, 0.5), vec3(1.0, 0.72, 0.22), sd / 0.38), 1.0 - smoothstep(0.36, 0.38, sd));
  vec2 mp = P - vec2(0.8, 3.1);
  float md = length(mp);
  c += vec3(0.8, 0.85, 1.0) * exp(-md * 3.0) * 0.25;
  c = mix(c, vec3(0.92, 0.92, 0.88) * (0.85 + 0.15 * snoise(vec3(mp * 9.0, 1.0))), 1.0 - smoothstep(0.29, 0.31, md));
  return c;
}
float ridgeAt(float x){ return 2.15 + 0.6 * abs(snoise(vec3(x * 0.28, 0.0, 2.0))) + 0.2 * snoise(vec3(x * 0.9, 1.0, 0.0)) - 0.45 * exp(-x * x * 0.4); }
// the universe through the window: sky, mountains, and water mirroring the sky
vec3 universe(vec2 P){
  float hz = 1.9;
  float rg = ridgeAt(P.x);
  if (P.y > rg) return sky(P);
  if (P.y > hz) return mix(vec3(0.16, 0.14, 0.36), vec3(0.6, 0.45, 0.6), smoothstep(rg - 0.1, rg, P.y) * 0.6) * (0.9 + 0.2 * snoise(vec3(P * 3.0, 0.0)));
  vec2 R = vec2(P.x + 0.025 * sin(P.y * 40.0 + uT * 1.5), 2.0 * hz - P.y);
  vec3 c = R.y > rg ? sky(R) : vec3(0.12, 0.11, 0.3);
  return c * vec3(0.66, 0.74, 0.92) + vec3(0.02, 0.05, 0.1) + vec3(0.7, 0.8, 1.0) * (1.0 - smoothstep(0.0, 0.03, hz - P.y)) * 0.3;
}
// a fish leaping from the water and flying across the sky
vec4 fish(vec2 P, float X0, float ts){
  float k = (uT - ts) / 4.5;
  if (k < 0.0 || k > 1.0) return vec4(0.0);
  vec2 F = vec2(X0 + 3.2 * k, 1.3 + 3.0 * sin(k * 2.83));
  vec2 d = normalize(vec2(3.2, 3.0 * 2.83 * cos(k * 2.83)));
  vec2 q = P - F;
  vec2 l = vec2(dot(q, d), dot(q, vec2(-d.y, d.x)));
  float body = 1.0 - smoothstep(0.9, 1.0, length(l / vec2(0.24, 0.08)));
  float tail = step(l.x, -0.18) * step(-0.36, l.x) * step(abs(l.y), (-0.18 - l.x) * 0.9);
  float flap = 0.6 + 0.4 * sin(uT * 14.0);
  float wing = step(0.05, abs(l.y)) * step(abs(l.y), 0.05 + 0.22 * flap) * step(abs(l.x + 0.03 + abs(l.y) * 0.45), 0.06);
  vec3 fc = mix(vec3(1.0, 0.55, 0.15), vec3(1.0, 0.85, 0.35), 0.5 + 0.5 * sin(l.x * 30.0 + uT * 4.0));
  fc = mix(fc, vec3(0.7, 0.85, 1.0), wing * (1.0 - body));
  float splash = (1.0 - smoothstep(0.0, 0.02, abs(length((P - vec2(X0, 1.85)) * vec2(1.0, 4.0)) - k * 2.4))) * (1.0 - smoothstep(0.0, 0.3, k)) * step(P.y, 1.9);
  return vec4(mix(fc, vec3(0.85, 0.92, 1.0), splash), max(max(body, tail), max(wing * 0.85, splash)));
}
// the cluttered room: piles of things, clocks, mirrors and frames
vec3 clutter(vec2 P){
  vec3 c = vec3(0.2, 0.15, 0.13) * (0.85 + 0.3 * snoise(vec3(P * 0.8, 3.0)));
  float cx = floor(P.x / 0.55);
  float hgt = 0.6 + 1.3 * hash1(cx) + 0.3 * snoise(vec3(cx, 0.0, 1.0));
  if (P.y < hgt) {
    float rh = 0.18 + 0.2 * hash1(cx + 2.0);
    float row = floor(P.y / rh);
    float hb = hash1(cx * 7.0 + row);
    vec3 bc = mix(vec3(0.38, 0.27, 0.2), vec3(0.47, 0.37, 0.33), hb);
    bc = mix(bc, vec3(0.44, 0.18, 0.15), step(0.8, hb));
    float edge = min(min(fract(P.y / rh), 1.0 - fract(P.y / rh)) * rh, min(fract(P.x / 0.55), 1.0 - fract(P.x / 0.55)) * 0.55);
    c = bc * (0.75 + 0.25 * smoothstep(0.0, 0.02, edge));
  } else if (P.y < 4.9) {
    vec2 g = vec2(P.x / 1.1, (P.y - 2.3) / 0.95);
    vec2 gc = floor(g);
    vec2 gf = (fract(g) - 0.5) * vec2(1.1, 0.95);
    float hh = hash2(gc + 5.0);
    float r = length(gf);
    if (hh < 0.4 && r < 0.32) {
      // a clock, its hands slowing to a stop as the window opens
      c = mix(vec3(0.9, 0.85, 0.72), vec3(0.35, 0.25, 0.15), smoothstep(0.26, 0.28, r));
      float a1 = uPhase * (0.5 + hh) + hh * 6.28, a2 = uPhase * 0.08 + hh * 3.0;
      vec2 h1 = vec2(sin(a1), cos(a1)), h2 = vec2(sin(a2), cos(a2));
      float hand = min(abs(dot(gf, vec2(h1.y, -h1.x))) + step(dot(gf, h1), 0.0) + step(0.22, dot(gf, h1)), abs(dot(gf, vec2(h2.y, -h2.x))) + step(dot(gf, h2), 0.0) + step(0.15, dot(gf, h2)));
      c = mix(c, vec3(0.1), 1.0 - smoothstep(0.006, 0.012, hand));
    } else if (hh < 0.7 && length(gf / vec2(0.3, 0.4)) < 1.0) {
      // a mirror, catching a dim sheen
      float sheen = smoothstep(0.03, 0.0, abs(gf.x + gf.y * 0.6 - 0.05)) * 0.5;
      c = mix(mix(vec3(0.45, 0.47, 0.5), vec3(0.25, 0.26, 0.3), gf.y + 0.5), vec3(0.6, 0.55, 0.45), smoothstep(0.88, 0.95, length(gf / vec2(0.3, 0.4)))) + sheen;
    } else if (hh >= 0.7 && abs(gf.x) < 0.32 && abs(gf.y) < 0.28) {
      c = mix(vec3(0.3, 0.22, 0.18), vec3(0.55, 0.42, 0.2), step(0.25, max(abs(gf.x) / 0.32, abs(gf.y) / 0.28) * 0.28));
    }
  }
  return c;
}
void main(){
  float n = snoise(vPos * 0.6) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  float open = uStill;
  float reach = (uT - 8.0) * 4.5;
  float settle = smoothstep(27.5, 31.5, uT);
  vec3 col;
  vec2 local;
  if (uSurface < 0.5) {
    float X = th * uRadius;
    vec2 P = vec2(X, vPos.y);
    vec3 room = clutter(P);
    room = mix(room, vec3(dot(room, vec3(0.33))), 0.5 * open);
    vec3 uni = universe(P);
    // the window, its two shutters swinging open
    vec2 wq = P - vec2(0.0, 2.35);
    float inWin = step(abs(wq.x), 1.0) * step(abs(wq.y), 1.12);
    float frameW = step(abs(wq.x), 1.08) * step(abs(wq.y), 1.2) * (1.0 - inWin);
    float shutter = step(1.0 - open, 1.0 - abs(wq.x)) * 0.0 + step(abs(wq.x), 1.0 - open) * inWin;
    vec3 wood = vec3(0.42, 0.28, 0.18) * (0.8 + 0.2 * step(0.5, fract(wq.y * 9.0))) * (0.85 + 0.15 * step(0.02, abs(wq.x)));
    vec3 view = mix(uni, wood, shutter);
    float beam = open * (1.0 - smoothstep(0.0, 2.5, length(wq * vec2(0.6, 1.0)))) * 0.5;
    room += vec3(1.0, 0.9, 0.65) * beam;
    room = mix(room, view, inWin);
    room = mix(room, vec3(0.3, 0.2, 0.13), frameW);
    float dW = length(wq * vec2(1.0, 1.2));
    col = mix(room, uni, smoothstep(reach, reach - 1.0, dW) * step(8.0, uT));
    col = mix(col, vec3(0.3, 0.2, 0.13), frameW * smoothstep(reach, reach - 1.0, dW) * (1.0 - smoothstep(10.0, 12.0, uT)));
    col += vec3(1.0, 0.9, 0.7) * exp(-pow(dW - reach, 2.0) * 3.0) * 0.6 * step(8.0, uT) * (1.0 - smoothstep(10.5, 12.5, uT));
    // fish leaping: two on their own, and one for every tap on the water
    vec4 f = fish(P, 0.35 * uRadius, 12.0);
    col = mix(col, f.rgb, f.a);
    f = fish(P, -0.5 * uRadius, 15.5);
    col = mix(col, f.rgb, f.a);
    for (int i = 0; i < 8; i++){
      if (uMark[i].w <= 0.0) continue;
      f = fish(P, atan(uMark[i].x, -uMark[i].z) * uRadius, uMark[i].w);
      col = mix(col, f.rgb, f.a);
    }
    local = vec2(X, vPos.y - uCy);
  } else if (uSurface < 1.5) {
    vec2 p = vPos.xz;
    vec3 floorC = mix(vec3(0.26, 0.18, 0.13), vec3(0.32, 0.22, 0.16), step(0.5, fract(p.x * 2.5))) * (0.85 + 0.2 * n);
    vec3 water = mix(vec3(0.08, 0.09, 0.28), vec3(0.2, 0.18, 0.45), n) + vec3(0.9, 0.9, 1.0) * glint(fract(p / 0.7) - 0.5, 0.5) * step(0.7, hash2(floor(p / 0.7))) * (0.5 + 0.5 * sin(uT * 2.0 + p.x));
    water += vec3(0.6, 0.7, 1.0) * (1.0 - smoothstep(0.0, 0.04, abs(fract(length(p) * 1.2 - uT * 0.15) - 0.5))) * 0.08;
    col = mix(floorC, water, smoothstep(reach, reach - 1.0, length(p - vec2(0.0, -uRadius))) * step(8.0, uT));
    local = vPos.xz;
  } else {
    vec3 starry = vec3(0.05, 0.06, 0.2) + vec3(1.0, 0.95, 0.85) * glint(fract(vPos.xz / 0.6) - 0.5, 0.6) * step(0.6, hash2(floor(vPos.xz / 0.6))) * 0.5;
    col = mix(vec3(0.13, 0.1, 0.09), starry, smoothstep(8.5, 11.5, uT));
    local = vPos.xz;
  }
  vec2 tile = vec2(uTileH * uAspect, uTileH);
  vec3 paint = tap(local / tile + 0.5);
  float drip = uSurface < 0.5 ? (10.0 - vPos.y) / 10.0 : 0.5 + length(vPos.xz) / (uRadius * 3.0);
  col = mix(col, paint, smoothstep(0.0, 0.25, settle * 1.3 - drip * 0.6 - n * 0.25));
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
